import React, {
  useEffect,
  useState,
} from "react";

import { useNavigate } from "react-router-dom";

import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

import {
  collection,
  doc,
  setDoc,
  getDoc,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";

import { db } from "../firebase";

const RAZORPAY_KEY_ID =
  process.env.REACT_APP_RAZORPAY_KEY_ID;

const PAYMENT_FUNCTION_URL =
  "https://asia-south1-eyal--demo.cloudfunctions.net/paymentTest";

/* =========================================================
   CHECKOUT
   FIREBASE ONLY
========================================================= */

export default function Checkout() {

  const navigate = useNavigate();

  const {
    cart,
    clearCart,
  } = useCart();

  const {
    user,
    userProfile,
    loading: authLoading,
  } = useAuth();


  /* =========================================================
     STATE
  ========================================================= */

  const [
    loading,
    setLoading,
  ] = useState(false);


  const [paymentMode, setPaymentMode] = useState("ONLINE");


  const [
    selectedDeliveryPerson,
    setSelectedDeliveryPerson,
  ] = useState(null);


  const [
    deliveryCharge,
    setDeliveryCharge,
  ] = useState(0);

  /* =========================================================
   DISCOUNT
========================================================= */

const [discountInfo] = useState(() => {
  try {
    const saved = sessionStorage.getItem(
      "cartDiscount"
    );

    if (!saved) {
      return {
        applied: false,
        percentage: 0,
        amount: 0,
      };
    }

    const parsed = JSON.parse(saved);

    return {
      applied: parsed.applied === true,
      percentage: 1,
      amount: Number(parsed.amount) || 0,
    };
  } catch {
    return {
      applied: false,
      percentage: 0,
      amount: 0,
    };
  }
});

const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const script = document.createElement("script");

    script.src =
      "https://checkout.razorpay.com/v1/checkout.js";

    script.onload = () => resolve(true);

    script.onerror = () => resolve(false);

    document.body.appendChild(script);
  });
};

  const [
    deliveryChargeLoading,
    setDeliveryChargeLoading,
  ] = useState(true);


  /* =========================================================
     AUTH + PROFILE GUARD
  ========================================================= */

  useEffect(() => {

    if (authLoading) {
      return;
    }


    if (!user) {

      sessionStorage.setItem(
        "redirectAfterLogin",
        "/checkout"
      );

      navigate("/login");

      return;

    }


    if (
      !userProfile?.name ||
      !userProfile?.mobile ||
      !userProfile?.address ||
      !userProfile?.area
    ) {

      navigate("/user/settings");

    }

  }, [
    user,
    userProfile,
    authLoading,
    navigate,
  ]);


  /* =========================================================
     LOAD SELECTED DELIVERY PERSON
  ========================================================= */

  useEffect(() => {

    const loadSelectedDeliveryPerson =
      async () => {

        try {

          const saved =
            sessionStorage.getItem(
              "selectedDeliveryPerson"
            );


          /*
           * Customer should normally never reach
           * checkout without selecting a person.
           */
          if (!saved) {

            navigate("/cart", {
              replace: true,
            });

            return;

          }


          const parsed =
            JSON.parse(saved);


          if (!parsed?.uid) {

            sessionStorage.removeItem(
              "selectedDeliveryPerson"
            );

            navigate("/cart", {
              replace: true,
            });

            return;

          }


          /*
           * Show stored person immediately.
           */
          setSelectedDeliveryPerson(
            parsed
          );


          /* =================================================
             READ LATEST DELIVERY PERSON FROM FIREBASE
          ================================================= */

          const deliveryRef =
            doc(
              db,
              "deliveryPersons",
              parsed.uid
            );


          const deliverySnap =
            await getDoc(
              deliveryRef
            );


          if (!deliverySnap.exists()) {

            throw new Error(
              "Delivery person profile not found."
            );

          }


          const firebasePerson =
            deliverySnap.data();


          /*
           * Make sure delivery person is approved.
           */
          const approved =
            firebasePerson.applicationStatus ===
              "APPROVED" ||
            firebasePerson.verified === true;


          if (!approved) {

            alert(
              "This delivery person is no longer approved."
            );

            sessionStorage.removeItem(
              "selectedDeliveryPerson"
            );

            navigate("/cart", {
              replace: true,
            });

            return;

          }


          /*
           * Latest delivery charge.
           */
          const charge =
            Number(
              firebasePerson.deliveryCharge || 0
            );


          setDeliveryCharge(
            Number.isFinite(charge)
              ? charge
              : 0
          );


          /*
           * Firebase remains source of truth.
           */
          const updatedPerson = {

            ...parsed,

            ...firebasePerson,

            uid:
              parsed.uid,

          };


          setSelectedDeliveryPerson(
            updatedPerson
          );


          /*
           * Keep sessionStorage current.
           */
          sessionStorage.setItem(
            "selectedDeliveryPerson",
            JSON.stringify(
              updatedPerson
            )
          );


        } catch (error) {

          console.error(
            "Failed to load delivery person:",
            error
          );


          alert(
            "Unable to load delivery person details."
          );


          sessionStorage.removeItem(
            "selectedDeliveryPerson"
          );


          navigate("/cart", {
            replace: true,
          });


        } finally {

          setDeliveryChargeLoading(
            false
          );

        }

      };


    loadSelectedDeliveryPerson();

  }, [
    navigate,
  ]);


  /* =========================================================
     DELIVERY PERSON REQUIRED
  ========================================================= */

  useEffect(() => {

    if (
      authLoading ||
      !user
    ) {
      return;
    }


    const timer =
      setTimeout(() => {

        const saved =
          sessionStorage.getItem(
            "selectedDeliveryPerson"
          );


        if (!saved) {

          navigate("/cart", {
            replace: true,
          });

        }

      }, 150);


    return () =>
      clearTimeout(timer);

  }, [
    authLoading,
    user,
    navigate,
  ]);


  /* =========================================================
     WAIT FOR REQUIRED DATA
  ========================================================= */

  if (
    authLoading ||
    !userProfile ||
    !selectedDeliveryPerson ||
    deliveryChargeLoading
  ) {

    return (
      <div
        style={
          styles.loadingPage
        }
      >

        <div
          style={
            styles.loadingIcon
          }
        >
          🚚
        </div>

        <h3>
          Loading checkout...
        </h3>

      </div>
    );

  }


  /* =========================================================
     EMPTY CART
  ========================================================= */

  if (!cart.length) {

    return (
      <div style={styles.empty}>

        <div
          style={
            styles.emptyIcon
          }
        >
          🛒
        </div>

        <h3>
          Your cart is empty
        </h3>

        <button
          style={
            styles.backBtn
          }
          onClick={() =>
            navigate("/cart")
          }
        >
          Back to Cart
        </button>

      </div>
    );

  }


  /* =========================================================
     CUSTOMER DATA
  ========================================================= */

  const {
    name,
    mobile,
    address,
    area,
  } = userProfile;


  /* =========================================================
   ORDER AMOUNTS + DISCOUNT
========================================================= */

const subtotal =
  cart.reduce(
    (sum, item) =>
      sum +
      Number(item.price || 0) *
      Number(item.qty || 0),
    0
  );

const finalDeliveryCharge =
  Number.isFinite(Number(deliveryCharge))
    ? Number(deliveryCharge)
    : 0;

// 1% discount on product subtotal
const discountAmount =
  discountInfo.applied
    ? Number(
        (subtotal * 0.01).toFixed(2)
      )
    : 0;

const total =
  subtotal +
  finalDeliveryCharge -
  discountAmount;


  /* =========================================================
     REFERRAL
  ========================================================= */

  let referral = null;


  try {

    referral =
      JSON.parse(
        localStorage.getItem(
          "eyalReferral"
        ) || "null"
      );

  } catch {

    referral = null;

  }


  /* =========================================================
     ITEMS PAYLOAD
  ========================================================= */

  const itemsPayload =
    cart.map(
      (item) => ({

        type:
          item.type ||
          "eyal",

        productId:
          item.product_id ||
          item.id ||
          "",

        shopId:
          item.shopId ||
          "",

        shopName:
          item.shopName ||
          "",

        ownerUid:
          item.ownerUid ||
          "EYAL",

        name:
          item.name ||
          "",

        brand:
          item.brand ||
          "",

        variantId:
          item.variantId ||
          "",

        color:
          item.color ||
          "",

        size:
          item.size ||
          "",

        price:
          Number(
            item.price || 0
          ),

        qty:
          Number(
            item.qty || 0
          ),

      })
    );


  /* =========================================================
     DELIVERY PERSON PAYLOAD
  ========================================================= */

  const deliveryPerson = {

    uid:
      selectedDeliveryPerson.uid ||
      "",

    name:
      selectedDeliveryPerson.name ||
      selectedDeliveryPerson.driverName ||
      "Delivery Person",

    mobile:
      selectedDeliveryPerson.mobile ||
      "",

    vehicleType:
      selectedDeliveryPerson.vehicleType ||
      selectedDeliveryPerson.autoType ||
      "",

    vehicleNumber:
      selectedDeliveryPerson.vehicleNumber ||
      selectedDeliveryPerson.autoNumber ||
      "",

    area:
      selectedDeliveryPerson.area ||
      selectedDeliveryPerson.serviceArea ||
      "",

    rating:
      selectedDeliveryPerson.rating ??
      0,

    deliveryCharge:
      finalDeliveryCharge,

    photo:
      selectedDeliveryPerson.photo ||
      selectedDeliveryPerson.photoURL ||
      "",

  };


  /* =========================================================
     CREATE ORDER + DEDUCT STOCK
     
     IMPORTANT:
     
     Everything happens inside ONE Firestore transaction:
     
     1. Read latest product stock
     2. Check requested quantity
     3. Deduct stock
     4. Create shopOrders
     5. Create deliveryRequests
     
     If anything fails, Firestore rolls back the
     entire transaction.
  ========================================================= */

  const createOrderTransaction = async (
  orderId,
  paymentDetails = {}
) => {

      /*
       * -----------------------------------------------------
       * STEP 1
       * GROUP CART ITEMS BY PRODUCT
       *
       * This prevents problems if the same product appears
       * more than once in the cart.
       * -----------------------------------------------------
       */

      const productQuantities = {};


      itemsPayload.forEach(
        (item) => {

          /*
           * Only shop products are deducted from
           * shopproducts.
           *
           * This keeps compatibility with possible
           * non-shop/Eyal items.
           */
          if (!item.shopId) {
            return;
          }


          const productId =
            item.productId;


          if (!productId) {

            throw new Error(
              `Product ID is missing for "${item.name}".`
            );

          }


          const qty =
            Number(
              item.qty || 0
            );


          if (
            !Number.isFinite(qty) ||
            qty <= 0
          ) {

            throw new Error(
              `Invalid quantity for "${item.name}".`
            );

          }


          if (
            !Number.isInteger(qty)
          ) {

            throw new Error(
              `Invalid quantity for "${item.name}".`
            );

          }


          if (
            !productQuantities[productId]
          ) {

            productQuantities[productId] = 0;

          }


          productQuantities[productId] +=
            qty;

        }
      );


      /*
       * -----------------------------------------------------
       * CREATE FIRESTORE REFERENCES
       * -----------------------------------------------------
       */

      const productRefs =
        Object.keys(
          productQuantities
        ).map(
          (productId) => ({

            productId,

            ref:
              doc(
                db,
                "shopproducts",
                productId
              ),

          })
        );


      /*
       * -----------------------------------------------------
       * CREATE RANDOM SHOP ORDER REFERENCES
       *
       * This preserves the previous behavior of addDoc()
       * while allowing the documents to be created inside
       * the transaction.
       * -----------------------------------------------------
       */

      const grouped = {};


      itemsPayload.forEach(
        (item) => {

          const ownerUid =
            item.ownerUid ||
            "EYAL";


          if (!grouped[ownerUid]) {

            grouped[ownerUid] = {

              ownerUid,

              shopId:
                item.shopId ||
                "",

              shopName:
                item.shopName ||
                "Eyal Mart",

              items: [],

            };

          }


          grouped[
            ownerUid
          ].items.push(
            item
          );

        }
      );


      const shopOrderRefs =
        Object.values(
          grouped
        ).map(
          (group) => ({

            group,

            ref:
              doc(
                collection(
                  db,
                  "shopOrders"
                )
              ),

          })
        );


      /*
       * Delivery request uses orderId as
       * its document ID.
       */
      const deliveryRequestRef =
        doc(
          db,
          "deliveryRequests",
          orderId
        );


      /*
       * -----------------------------------------------------
       * FIRESTORE TRANSACTION
       * -----------------------------------------------------
       */

      await runTransaction(
        db,
        async (transaction) => {

          /* ===============================================
             READ ALL PRODUCT DOCUMENTS FIRST

             Firestore requires transaction reads to happen
             before transaction writes.
          =============================================== */

          const productSnapshots = {};


          for (
            const productInfo of productRefs
          ) {

            const snapshot =
              await transaction.get(
                productInfo.ref
              );


            if (!snapshot.exists()) {

              const cartItem =
                itemsPayload.find(
                  (item) =>
                    item.productId ===
                    productInfo.productId
                );


              throw new Error(
                `Product "${cartItem?.name || productInfo.productId}" is no longer available.`
              );

            }


            productSnapshots[
              productInfo.productId
            ] = snapshot;

          }


          /* ===============================================
             CHECK STOCK
          =============================================== */

          const stockUpdates = [];


          for (
            const productInfo of productRefs
          ) {

            const snapshot =
              productSnapshots[
                productInfo.productId
              ];


            const productData =
              snapshot.data();


            const currentStock =
              Number(
                productData.stock ?? 0
              );


            const requestedQty =
              productQuantities[
                productInfo.productId
              ];


            /*
             * Invalid stock should be treated as zero.
             */
            const safeStock =
              Number.isFinite(
                currentStock
              )
                ? Math.max(
                    0,
                    Math.floor(
                      currentStock
                    )
                  )
                : 0;


            /*
             * IMPORTANT:
             *
             * Do not allow order when requested
             * quantity is greater than available stock.
             */
            if (
              requestedQty >
              safeStock
            ) {

              const cartItem =
                itemsPayload.find(
                  (item) =>
                    item.productId ===
                    productInfo.productId
                );


              throw new Error(
                `Only ${safeStock} "${cartItem?.name || "item"}" available. You requested ${requestedQty}.`
              );

            }


            const newStock =
              safeStock -
              requestedQty;


            stockUpdates.push({

              ref:
                productInfo.ref,

              newStock,

            });

          }


          /* ===============================================
             WRITE STOCK UPDATES
          =============================================== */

          stockUpdates.forEach(
            (update) => {

              transaction.update(
                update.ref,
                {

                  stock:
                    update.newStock,

                  updatedAt:
                    serverTimestamp(),

                }
              );

            }
          );


          /* ===============================================
             CREATE SHOP ORDERS
          =============================================== */

          shopOrderRefs.forEach(
            (shopOrderInfo) => {

              const group =
                shopOrderInfo.group;


              const shopTotal =
                group.items.reduce(
                  (
                    sum,
                    item
                  ) =>
                    sum +
                    Number(
                      item.price || 0
                    ) *
                    Number(
                      item.qty || 0
                    ),
                  0
                );


              const shopOrder = {

                /* =============================
                   ORDER
                ============================= */

                orderId:
                  orderId,


                /* =============================
                   SHOP
                ============================= */

                ownerUid:
                  group.ownerUid,

                shopId:
                  group.shopId,

                shopName:
                  group.shopName,


                /* =============================
                   CUSTOMER
                ============================= */

                customerUid:
                  user.uid,

                customerName:
                  name,

                mobile:
                  mobile,

                address:
                  address,

                area:
                  area,


                /* =============================
                   PAYMENT
                ============================= */

               paymentType:
  paymentDetails.paymentType || "Cash on Delivery",

paymentStatus:
  paymentDetails.paymentStatus || "PENDING",

razorpayPaymentId:
  paymentDetails.razorpayPaymentId || "",

razorpayOrderId:
  paymentDetails.razorpayOrderId || "",

razorpaySignature:
  paymentDetails.razorpaySignature || "",


                /* =============================
   AMOUNT
============================= */

// Product subtotal for this shop
subtotal: shopTotal,

// Apply 1% discount proportionally to this shop
discountPercentage:
  discountInfo.applied ? 1 : 0,

discountAmount:
  discountInfo.applied
    ? Number(
        (
          shopTotal *
          0.01
        ).toFixed(2)
      )
    : 0,

deliveryCharge:
  finalDeliveryCharge,

// Final amount after discount + delivery charge
total:
  Number(
    (
      shopTotal -
      (
        discountInfo.applied
          ? shopTotal * 0.01
          : 0
      )
    ).toFixed(2)
  ),


                /* =============================
                   ORDER STATUS
                ============================= */

                status:
                  "New",


                /* =============================
                   DELIVERY PERSON
                ============================= */

                deliveryPersonUid:
                  deliveryPerson.uid,

                deliveryPersonName:
                  deliveryPerson.name,

                deliveryPersonMobile:
                  deliveryPerson.mobile,

                deliveryPersonVehicleType:
                  deliveryPerson.vehicleType,

                deliveryPersonVehicleNumber:
                  deliveryPerson.vehicleNumber,

                deliveryPersonArea:
                  deliveryPerson.area,

                deliveryPersonPhoto:
                  deliveryPerson.photo,

                deliveryStatus:
                  "PENDING",


                /* =============================
                   ITEMS
                ============================= */

                items:
                  group.items,


                /* =============================
                   REFERRAL
                ============================= */

                referredBy:
                  referral?.volunteerUid ||
                  "",

                referralProduct:
                  referral?.productId ||
                  "",

                referralTime:
                  referral?.referredAt ||
                  "",


                /* =============================
                   TIME
                ============================= */

                createdAt:
                  serverTimestamp(),

                updatedAt:
                  serverTimestamp(),

              };


              transaction.set(
                shopOrderInfo.ref,
                shopOrder
              );

            }
          );


          /* ===============================================
             CREATE DELIVERY REQUEST
          =============================================== */

          const requestData = {

            /* =============================
               ORDER
            ============================= */

            orderId:
              orderId,


            /* =============================
               CUSTOMER
            ============================= */

            customerUid:
              user.uid,

            customerName:
              name,

            customerMobile:
              mobile,

            address:
              address,

            area:
              area,


          /* =============================
   ORDER AMOUNT
============================= */

subtotal: subtotal,

discountPercentage:
  discountInfo.applied ? 1 : 0,

discountAmount:
  discountAmount,

deliveryCharge:
  finalDeliveryCharge,

total:
  total,


            /* =============================
               PAYMENT
            ============================= */

          paymentType:
  paymentDetails.paymentType || "Cash on Delivery",

paymentStatus:
  paymentDetails.paymentStatus || "PENDING",

razorpayPaymentId:
  paymentDetails.razorpayPaymentId || "",

razorpayOrderId:
  paymentDetails.razorpayOrderId || "",

razorpaySignature:
  paymentDetails.razorpaySignature || "",


            /* =============================
               ITEMS
            ============================= */

            items:
              itemsPayload,


            /* =============================
               DELIVERY PERSON
            ============================= */

            deliveryPersonUid:
              deliveryPerson.uid,

            deliveryPersonName:
              deliveryPerson.name,

            deliveryPersonMobile:
              deliveryPerson.mobile,

            deliveryPersonVehicleType:
              deliveryPerson.vehicleType,

            deliveryPersonVehicleNumber:
              deliveryPerson.vehicleNumber,

            deliveryPersonArea:
              deliveryPerson.area,

            deliveryPersonPhoto:
              deliveryPerson.photo,

            deliveryPersonCharge:
              finalDeliveryCharge,


            /* =============================
               REQUEST STATUS
            ============================= */

            status:
              "PENDING",

            deliveryStatus:
              "PENDING",


            /* =============================
               TIMESTAMPS
            ============================= */

            createdAt:
              serverTimestamp(),

            updatedAt:
              serverTimestamp(),

          };


          transaction.set(
            deliveryRequestRef,
            requestData
          );

        }
      );

    };


  /* =========================================================
     FINISH ORDER
  ========================================================= */

  const finishOrder =
    (orderId) => {

      clearCart();


      localStorage.removeItem(
        "eyalReferral"
      );


      sessionStorage.removeItem(
        "selectedDeliveryPerson"
      );


      sessionStorage.setItem(
        "lastOrderId",
        orderId
      );


      navigate(
        "/success",
        {
          state: {
            orderId,
          },
        }
      );

    };


  /* =========================================================
     PLACE COD ORDER
  ========================================================= */
const placeOrder = async () => {
  if (loading || !cart.length) {
    return;
  }

  if (!deliveryPerson.uid) {
    alert("Please select a delivery person.");
    navigate("/cart");
    return;
  }

  if (
    !Number.isFinite(finalDeliveryCharge) ||
    finalDeliveryCharge < 0
  ) {
    alert("Invalid delivery charge.");
    return;
  }

  for (const item of itemsPayload) {
    const qty = Number(item.qty || 0);

    if (!Number.isInteger(qty) || qty <= 0) {
      alert(
        `Invalid quantity for "${item.name || "product"}".`
      );
      return;
    }
  }

  if (
    paymentMode === "RAZORPAY" &&
    !RAZORPAY_KEY_ID
  ) {
    alert(
      "Online payment is not configured. Please use Cash on Delivery."
    );
    return;
  }

  setLoading(true);

  const orderId = "ORD" + Date.now();

  try {
    /*
     * CASH ON DELIVERY
     */
    if (paymentMode === "COD") {
      await createOrderTransaction(orderId, {
        paymentType: "Cash on Delivery",
        paymentStatus: "PENDING",
      });

      console.log(
        "COD Firebase order created:",
        orderId
      );

      finishOrder(orderId);
      return;
    }

    /*
     * ONLINE PAYMENT
     */
    const razorpayLoaded =
      await loadRazorpayScript();

    if (!razorpayLoaded) {
      throw new Error(
        "Unable to load Razorpay. Please check your internet connection."
      );
    }

    /*
     * Create Razorpay order on Firebase Function
     *
     * The backend should convert rupees to paise.
     */
    const createPaymentResponse =
      await fetch(
        `${PAYMENT_FUNCTION_URL}/createOrder`,
        {
          method: "POST",

          headers: {
            "Content-Type": "application/json",
          },

          body: JSON.stringify({
            amount: Number(total.toFixed(2)),
          }),
        }
      );

    if (!createPaymentResponse.ok) {
      throw new Error(
        "Unable to create online payment order."
      );
    }

   const responseText =
  await createPaymentResponse.text();

console.log(
  "Razorpay createOrder HTTP status:",
  createPaymentResponse.status
);

console.log(
  "Razorpay createOrder raw response:",
  responseText
);

let razorpayOrder;

try {
  razorpayOrder = JSON.parse(responseText);
} catch (error) {
  throw new Error(
    "Payment server returned invalid JSON."
  );
}

console.log(
  "Razorpay createOrder parsed response:",
  razorpayOrder
);

/*
 * Supports these possible backend response formats:
 *
 * { id: "order_xxx" }
 * { order: { id: "order_xxx" } }
 * { data: { id: "order_xxx" } }
 */
const razorpayOrderData =
  razorpayOrder?.id
    ? razorpayOrder
    : razorpayOrder?.order?.id
      ? razorpayOrder.order
      : razorpayOrder?.data?.id
        ? razorpayOrder.data
        : null;

if (!razorpayOrderData?.id) {
  throw new Error(
    razorpayOrder?.error ||
    razorpayOrder?.message ||
    "Invalid Razorpay order response. Check Firebase Function response."
  );
}

    /*
     * Open Razorpay checkout
     */
    const paymentResult =
      await new Promise((resolve, reject) => {
        const options = {
  key: RAZORPAY_KEY_ID,

  amount:
    razorpayOrderData.amount ||
    Math.round(total * 100),

  currency:
    razorpayOrderData.currency || "INR",

  name: "Eyal App",

  description:
    `Order payment ${orderId}`,

  order_id:
    razorpayOrderData.id,

  prefill: {
    name: name || "",
    contact: mobile || "",
  },

  notes: {
    eyalOrderId: orderId,
  },

  theme: {
    color: "#16a34a",
  },

  handler: async function (response) {
    try {
      const verifyResponse =
        await fetch(
          `${PAYMENT_FUNCTION_URL}/verifyPayment`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              razorpay_payment_id:
                response.razorpay_payment_id,

              razorpay_order_id:
                response.razorpay_order_id,

              razorpay_signature:
                response.razorpay_signature,
            }),
          }
        );

      const verifyResult =
        await verifyResponse.json();

      console.log(
        "Razorpay verification response:",
        verifyResult
      );

      if (
        !verifyResponse.ok ||
        verifyResult.success !== true
      ) {
        throw new Error(
          verifyResult.error ||
          verifyResult.message ||
          "Payment verification failed."
        );
      }

      resolve({
        paymentType: "Online Payment",
        paymentStatus: "PAID",
        razorpayPaymentId:
          response.razorpay_payment_id,
        razorpayOrderId:
          response.razorpay_order_id,
        razorpaySignature:
          response.razorpay_signature,
      });
    } catch (error) {
      reject(error);
    }
  },

  modal: {
    ondismiss: function () {
      reject(
        new Error(
          "Online payment was cancelled."
        )
      );
    },
  },
};

        const razorpay =
          new window.Razorpay(options);

        razorpay.on(
          "payment.failed",
          function (response) {
            reject(
              new Error(
                response.error?.description ||
                  "Payment failed."
              )
            );
          }
        );

        razorpay.open();
      });

    /*
     * Create Firebase order only after
     * successful payment verification
     */
    await createOrderTransaction(
      orderId,
      paymentResult
    );

    console.log(
      "Online payment order created:",
      orderId
    );

    finishOrder(orderId);
  } catch (error) {
    console.error(
      "Order/payment failed:",
      error
    );

    alert(
      error.message ||
        "Payment or order failed. Please try again."
    );
  } finally {
    setLoading(false);
  }
};


  /* =========================================================
     UI
  ========================================================= */

  return (

    <div
      style={
        styles.page
      }
    >

      <div
        style={
          styles.card
        }
      >

        {/* =================================================
            TITLE
        ================================================= */}

        <h3
          style={
            styles.title
          }
        >
          Checkout
        </h3>


        {/* =================================================
            CUSTOMER DETAILS
        ================================================= */}

        <div
          style={
            styles.block
          }
        >

          <div
            style={
              styles.sectionTitle
            }
          >
            Delivery Address
          </div>


          <strong>
            {name}
          </strong>


          <div
            style={
              styles.muted
            }
          >
            {mobile}
          </div>


          <div
            style={
              styles.muted
            }
          >
            {address}, {area}
          </div>

        </div>


        {/* =================================================
            SELECTED DELIVERY PERSON
        ================================================= */}

        <div
          style={
            styles.deliveryBlock
          }
        >

          <div
            style={
              styles.deliveryHeader
            }
          >

            <div>

              <div
                style={
                  styles.sectionTitle
                }
              >
                Delivery Person
              </div>


              <div
                style={
                  styles.selectedStatus
                }
              >
                ✓ Selected
              </div>

            </div>


            <div
              style={
                styles.deliveryIcon
              }
            >
              🚚
            </div>

          </div>


          {/* DELIVERY PERSON CARD */}

          <div
            style={
              styles.deliveryCard
            }
          >

            {/* PROFILE PHOTO */}

            <div
              style={
                styles.deliveryAvatar
              }
            >

              {deliveryPerson.photo ? (

                <img
                  src={
                    deliveryPerson.photo
                  }
                  alt="Delivery Person"
                  style={
                    styles.deliveryPhoto
                  }
                />

              ) : (

                <span>
                  🚚
                </span>

              )}

            </div>


            {/* DETAILS */}

            <div
              style={{
                flex: 1,
              }}
            >

              <div
                style={
                  styles.deliveryName
                }
              >
                {
                  deliveryPerson.name
                }
              </div>


              {deliveryPerson.mobile && (

                <div
                  style={
                    styles.deliveryMuted
                  }
                >
                  📞{" "}
                  {
                    deliveryPerson.mobile
                  }
                </div>

              )}


              {deliveryPerson.vehicleType && (

                <div
                  style={
                    styles.deliveryMuted
                  }
                >
                  🛵{" "}
                  {
                    deliveryPerson.vehicleType
                  }

                  {deliveryPerson.vehicleNumber
                    ? ` • ${deliveryPerson.vehicleNumber}`
                    : ""}

                </div>

              )}


              {deliveryPerson.area && (

                <div
                  style={
                    styles.deliveryMuted
                  }
                >
                  📍{" "}
                  {
                    deliveryPerson.area
                  }
                </div>

              )}

            </div>


            {/* SELECTED */}

            <div
              style={
                styles.checkCircle
              }
            >
              ✓
            </div>

          </div>


          {/* CHANGE PERSON */}

          <button
            style={
              styles.changeBtn
            }
            onClick={() =>
              navigate("/cart")
            }
            disabled={loading}
          >
            Change Delivery Person
          </button>

        </div>


        {/* =================================================
            DELIVERY CHARGE
        ================================================= */}

        <div
          style={
            styles.chargeBox
          }
        >

          <div
            style={
              styles.chargeRow
            }
          >

            <div>

              <div
                style={
                  styles.chargeTitle
                }
              >
                🚚 Delivery Charge
              </div>

              <div
                style={
                  styles.chargeDescription
                }
              >
                Delivery charge set by{" "}
                {
                  deliveryPerson.name
                }
              </div>

            </div>


            <strong
              style={
                styles.chargeAmount
              }
            >
              ₹{finalDeliveryCharge}
            </strong>

          </div>

        </div>


        {/* =================================================
            PAYMENT
        ================================================= */}

      <div style={styles.block}>
  <div style={styles.sectionTitle}>
    Payment
  </div>

  <label
    style={{
      ...styles.radio,
      ...(paymentMode === "ONLINE"
        ? {
            border: "1px solid #1677ff",
            background: "#eff6ff",
            color: "#0f4fc4",
          }
        : {}),
    }}
  >
    <input
      type="radio"
      name="paymentMode"
      value="ONLINE"
      checked={paymentMode === "ONLINE"}
      onChange={(e) =>
        setPaymentMode(e.target.value)
      }
      style={styles.radioInput}
    />

    <span style={styles.radioText}>
      Online Payment
    </span>
  </label>

  <label
    style={{
      ...styles.radio,
      ...(paymentMode === "COD"
        ? {
            border: "1px solid #1677ff",
            background: "#eff6ff",
            color: "#0f4fc4",
          }
        : {}),
    }}
  >
    <input
      type="radio"
      name="paymentMode"
      value="COD"
      checked={paymentMode === "COD"}
      onChange={(e) =>
        setPaymentMode(e.target.value)
      }
      style={styles.radioInput}
    />

    <span style={styles.radioText}>
      Cash on Delivery
    </span>
  </label>
</div>


        {/* =================================================
            ORDER SUMMARY
        ================================================= */}

        <div
          style={
            styles.summary
          }
        >

          {/* SUBTOTAL */}

          <div
            style={
              styles.summaryRow
            }
          >

            <span>
              Subtotal
            </span>

            <span>
  ₹{subtotal.toFixed(2)}
</span>

          </div>

{/* DISCOUNT */}

{discountAmount > 0 && (
  <div
    style={{
      ...styles.summaryRow,
      color: "#16a34a",
      fontWeight: 600,
    }}
  >
    <span>
      Discount (1%)
    </span>

    <span>
      −₹{discountAmount.toFixed(2)}
    </span>
  </div>
)}

          {/* DELIVERY */}

          <div
            style={
              styles.summaryRow
            }
          >

            <span>
              🚚 Delivery Charge
            </span>

            <span>
              ₹{finalDeliveryCharge}
            </span>

          </div>


          {/* TOTAL */}

          <div
            style={
              styles.totalRow
            }
          >

            <strong>
              Total Amount
            </strong>

            <span>
  ₹{total.toFixed(2)}
</span>

          </div>

        </div>


        {/* =================================================
            PLACE ORDER
        ================================================= */}

        <button
  type="button"
  onClick={placeOrder}
  disabled={loading}
>
  {loading
    ? "Placing Order..."
    : paymentMode === "ONLINE"
      ? "Pay Online & Place Order"
      : "Place Order"}
</button>


      </div>

    </div>

  );

}


/* =========================================================
   STYLES
========================================================= */

const styles = {

  page: {
    minHeight:
      "100dvh",

    background:
      "#f9fafb",

    display:
      "flex",

    justifyContent:
      "center",

    alignItems:
      "flex-start",

    padding:
      "70px 16px 90px",

    boxSizing:
      "border-box",

    overflowY:
      "auto",
  },


  card: {
    width:
      "100%",

    maxWidth:
      420,

    background:
      "#fff",

    borderRadius:
      14,

    padding:
      20,

    boxShadow:
      "0 5px 20px rgba(0,0,0,0.08)",

    boxSizing:
      "border-box",
  },


  title: {
    marginBottom:
      18,

    fontSize:
      20,
  },


  block: {
    marginBottom:
      18,
  },


  sectionTitle: {
    fontWeight:
      600,

    fontSize:
      15,
  },


  muted: {
    color:
      "#6b7280",

    fontSize:
      13,

    marginTop:
      3,
  },


  /* =======================================================
     DELIVERY PERSON
  ======================================================= */

  deliveryBlock: {
    background:
      "#f8fafc",

    border:
      "1px solid #e2e8f0",

    borderRadius:
      14,

    padding:
      14,

    marginBottom:
      18,
  },


  deliveryHeader: {
    display:
      "flex",

    alignItems:
      "center",

    justifyContent:
      "space-between",

    marginBottom:
      12,
  },


  selectedStatus: {
    color:
      "#16a34a",

    fontSize:
      13,

    fontWeight:
      700,

    marginTop:
      4,
  },


  deliveryIcon: {
    fontSize:
      32,
  },


  deliveryCard: {
    background:
      "#fff",

    border:
      "1px solid #e2e8f0",

    borderRadius:
      12,

    padding:
      12,

    display:
      "flex",

    alignItems:
      "center",

    gap:
      12,
  },


  deliveryAvatar: {
    width:
      46,

    height:
      46,

    borderRadius:
      "50%",

    background:
      "#eff6ff",

    display:
      "flex",

    alignItems:
      "center",

    justifyContent:
      "center",

    fontSize:
      23,

    overflow:
      "hidden",

    flexShrink:
      0,
  },


  deliveryPhoto: {
    width:
      "100%",

    height:
      "100%",

    objectFit:
      "cover",
  },


  deliveryName: {
    fontWeight:
      700,

    fontSize:
      15,

    marginBottom:
      4,
  },


  deliveryMuted: {
    color:
      "#6b7280",

    fontSize:
      12,

    marginTop:
      3,
  },


  checkCircle: {
    width:
      28,

    height:
      28,

    borderRadius:
      "50%",

    background:
      "#16a34a",

    color:
      "#fff",

    display:
      "flex",

    alignItems:
      "center",

    justifyContent:
      "center",

    fontWeight:
      700,

    flexShrink:
      0,
  },


  changeBtn: {
    width:
      "100%",

    marginTop:
      10,

    height:
      38,

    borderRadius:
      9,

    border:
      "none",

    background:
      "transparent",

    color:
      "#2563eb",

    fontWeight:
      600,

    cursor:
      "pointer",
  },


  /* =======================================================
     DELIVERY CHARGE
  ======================================================= */

  chargeBox: {
    background:
      "#f0fdf4",

    border:
      "1px solid #bbf7d0",

    borderRadius:
      12,

    padding:
      13,

    marginBottom:
      18,
  },


  chargeRow: {
    display:
      "flex",

    alignItems:
      "center",

    justifyContent:
      "space-between",

    gap:
      10,
  },


  chargeTitle: {
    fontWeight:
      700,

    fontSize:
      14,

    color:
      "#166534",
  },


  chargeDescription: {
    fontSize:
      11,

    color:
      "#6b7280",

    marginTop:
      3,
  },


  chargeAmount: {
    fontSize:
      17,

    color:
      "#15803d",

    whiteSpace:
      "nowrap",
  },


  /* =======================================================
     PAYMENT
  ======================================================= */

    /* =======================================================
     PAYMENT RADIO BUTTON
  ======================================================= */

  radio: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    width: "100%",
    minHeight: 42,
    padding: "10px 12px",
    marginBottom: 10,
    boxSizing: "border-box",

    border: "1px solid #e5e7eb",
    borderRadius: 10,

    background: "#ffffff",
    color: "#222222",

    fontSize: 15,
    fontWeight: 500,

    cursor: "pointer",
  },

  radioInput: {
    width: 17,
    height: 17,
    margin: 0,
    flexShrink: 0,

    accentColor: "#1677ff",
    cursor: "pointer",
  },

  radioText: {
    flex: 1,
    lineHeight: 1.4,
  },


  /* =======================================================
     SUMMARY
  ======================================================= */

  summary: {
    borderTop:
      "1px solid #e5e7eb",

    paddingTop:
      12,

    marginBottom:
      20,
  },


  summaryRow: {
    display:
      "flex",

    justifyContent:
      "space-between",

    alignItems:
      "center",

    marginBottom:
      9,

    fontSize:
      14,

    color:
      "#374151",
  },


  totalRow: {
    display:
      "flex",

    justifyContent:
      "space-between",

    alignItems:
      "center",

    marginTop:
      12,

    paddingTop:
      12,

    borderTop:
      "1px solid #e5e7eb",

    fontSize:
      18,

    color:
      "#111827",
  },


  /* =======================================================
     BUTTON
  ======================================================= */

  btn: {
    width:
      "100%",

    height:
      46,

    borderRadius:
      12,

    border:
      "none",

    background:
      "#16a34a",

    color:
      "#fff",

    fontWeight:
      600,

    fontSize:
      15,

    cursor:
      "pointer",
  },


  /* =======================================================
     LOADING
  ======================================================= */

  loadingPage: {
    minHeight:
      "100dvh",

    display:
      "flex",

    flexDirection:
      "column",

    alignItems:
      "center",

    justifyContent:
      "center",

    background:
      "#f9fafb",

    gap:
      10,
  },


  loadingIcon: {
    fontSize:
      40,
  },


  /* =======================================================
     EMPTY
  ======================================================= */

  empty: {
    minHeight:
      "100dvh",

    display:
      "flex",

    flexDirection:
      "column",

    justifyContent:
      "center",

    alignItems:
      "center",

    gap:
      15,

    background:
      "#f9fafb",
  },


  emptyIcon: {
    fontSize:
      45,
  },


  backBtn: {
    padding:
      "10px 18px",

    borderRadius:
      9,

    border:
      "none",

    background:
      "#2563eb",

    color:
      "#fff",

    fontWeight:
      600,

    cursor:
      "pointer",
  },

  

};